import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { dict, resolve } from "@/i18n/translations";

const LanguageContext = createContext(null);

const STORAGE_KEY = "csm_lang";

function detectInitialLang() {
  // SEO-first: default to Spanish (primary market). Only switch to EN when
  // the user explicitly toggles it — this keeps a consistent Hreflang/og:locale
  // signal on cold loads and avoids the browser's Accept-Language flipping the
  // crawl into EN.
  if (typeof window === "undefined") return "es";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && dict[stored]) return stored;
  return "es";
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState("es");

  // Hydrate from localStorage/browser once on mount (avoids SSR mismatch).
  useEffect(() => {
    setLangState(detectInitialLang());
  }, []);

  const setLang = useCallback((next) => {
    if (!dict[next]) return;
    setLangState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  // Sync <html lang> with the active language. The page <title> is owned by
  // each route's <Seo> component (with the index.html default as fallback),
  // so we intentionally don't touch document.title here anymore.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const meta = dict[lang].meta;
    document.documentElement.lang = meta.html_lang;
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      toggleLang: () => setLang(lang === "es" ? "en" : "es"),
      t: (key) => resolve(lang, key),
    }),
    [lang, setLang]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
